import { useState, useCallback, useEffect } from "react";
import { Box, Typography, TextField, Button, MenuItem, useTheme } from "@mui/material";
import dayjs from "dayjs";
import axios from "axios";
import { tokens } from "../../theme";
import SalesTable from "../../components/SalesTable";

const SalesReport = () => {
  const theme = useTheme();
   const today = dayjs().format("YYYY-MM-DD");
  const colors = tokens(theme.palette.mode);

  const [salesData, setSalesData] = useState([]);
  const [summary, setSummary] = useState({});
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [userId, setUserId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [itemId, setItemId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [credit, setCredit] = useState("");

  // --- Fetch dropdown data ---
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user`);
      const allUsers = res.data || [];

      const salesUsers = allUsers.filter(
        (u) => u.Role?.name?.toLowerCase().includes("sale")
      );

      setUsers(salesUsers);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/customer`);
      setCustomers(res.data || []);
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  }, []);



  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/item`);
      setItems(res.data || []);
    } catch (err) {
      console.error("Error fetching items:", err);
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/warehouse`);
      setWarehouses(res.data || []);
    } catch (err) {
      console.error("Error fetching warehouses:", err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
    fetchItems();
    fetchWarehouses();
  }, [fetchUsers, fetchCustomers, fetchItems, fetchWarehouses]);

  // --- Fetch report ---
const fetchReport = useCallback(async () => {
  setLoading(true);
  try {
    const payload = {};
    if (startDate) payload.startDate = startDate;
    if (endDate) payload.endDate = endDate;
    if (userId) payload.userId = userId;
    if (customerId) payload.customerId = customerId;
    if (itemId) payload.itemId = itemId;
    if (warehouseId) payload.warehouseId = warehouseId;
    if (credit) payload.credit = credit === "true";

    const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/sales/report`, payload);
    const data = res.data;

    // --- Map sales data for table ---
    const formattedRows = data.sales?.map((sale) => ({
  id: sale.id,
  salesman: sale.User?.fullName || "-",
  date: sale.salesDate ? dayjs.utc(sale.salesDate).format("YYYY-MM-DD") : "-",
  saleQty: sale.totalQuantity || 0,
  totalPrice: sale.totalTaxedPrice || 0,
  paidAmount: sale.paidAmount || 0,
  warehouse: sale.Warehouse?.name || "-",
  credit: sale.credit || false,
  creditAmount: Number(sale.creditAmount || 0).toFixed(2),
  customer: sale.Customer?.name || "-",
  SalesItems: sale.SalesItems || [],  // <- keep the full array
}));


    // --- Update summary including totalCredit ---
    setSalesData(formattedRows);
    setSummary({
      totalSales: data.totalSales || 0,
      totalPaid: data.totalPaid || 0,
      totalItemsSold: data.totalItemsSold || 0,
      totalBonusAmount: data.totalBonusAmount || 0,
      totalSponsorAmount: data.totalSponsorAmount || 0,
      totalStockout: data.totalStockout || 0,
      totalReturn: data.totalReturn || 0,
      totalLending: data.totalLending || 0,
      salesCount: data.salesCount || 0,
      totalCredit: data.totalCredit || 0,
    });
  } catch (err) {
    console.error("Error fetching report:", err);
    setSalesData([]);
    setSummary({});
  } finally {
    setLoading(false);
  }
}, [startDate, endDate, userId, customerId, itemId, warehouseId,credit]);


useEffect(() => {
  fetchReport();
}, [fetchReport]);

  // --- Table columns ---
  const columns = [
    { field: "salesman", headerName: "Salesman Name", width: 180 },
    {
      field: "date",
      headerName: "Sales Date",
      width: 130,
      valueFormatter: ({ value }) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
    },
    { field: "stockoutItem", headerName: "Stock Out Item", width: 150 },
    { field: "stockoutQty", headerName: "Stock Out Qty", width: 100 },
    { field: "saleQty", headerName: "Sold Qty", width: 80 },
    { field: "returnQty", headerName: "Return Qty", width: 80 },
    { field: "totalPrice", headerName: "Total Price", width: 120 },
    { field: "paidAmount", headerName: "Paid Amount", width: 120 },
    { field: "bank", headerName: "Bank", width: 180 },
    { field: "lendingQty", headerName: "Lending Qty", width: 120 },
    { field: "warehouse", headerName: "Warehouse", width: 150 },
    { field: "bonus", headerName: "Bonus Qty", width: 120 },
    { field: "bonusAmount", headerName: "Bonus Amount", width: 120 },
    { field: "sponsor", headerName: "Sponsur Qty", width: 120 },
    { field: "sponsurAmount", headerName: "Sponsur Amount", width: 120 },
    { field: "customer", headerName: "Sponsur Qty", width: 120 },
    { 
      field: "credit", 
      headerName: "Credit", 
      width: 100,
      valueFormatter: ({ value }) => (value ? "Yes" : "No")
    },
    { 
      field: "creditAmount", 
      headerName: "Credit Amount", 
      width: 130,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2)
    },
  ];



  return (
    <Box p={3}>
      <Typography variant="h4" mb={2}>
        Sales Report
      </Typography>

      {/* --- Filters --- */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        {/* Salesperson filter */}
        <TextField
          select
          label="Salesperson"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.fullName || u.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Customer filter */}
        <TextField
          select
          label="Customer"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {customers.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>

        
        <TextField
          select
          label="Warehouse"
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
        select
        label="Credit"
        value={credit}
        onChange={(e) => setCredit(e.target.value)}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="true">Credit Only</MenuItem>
        <MenuItem value="false">Non-Credit Only</MenuItem>
      </TextField>


        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </Box>

      {/* --- Summary Row --- */}
      <Box display="flex" gap={1} mb={3} flexWrap="nowrap" overflow="auto">
        {[
          { label: "Sales Count", value: summary.salesCount },
          { label: "Total Sales", value: Number(summary.totalSales || 0).toFixed(2) },
          { label: "Total Paid", value: Number(summary.totalPaid || 0).toFixed(2) },
          { label: "Total Bonus Amount", value: Number(summary.totalBonusAmount || 0).toFixed(2) },
          { label: "Total Sponsor Amount", value: Number(summary.totalSponsorAmount || 0).toFixed(2) },
          { label: "Total Credit", value: Number(summary.totalCredit || 0).toFixed(2) },
          { label: "Total Items Sold", value: summary.totalItemsSold },
          { label: "Total Stockout", value: summary.totalStockout },
          { label: "Total Return", value: summary.totalReturn },
          { label: "Total Lending", value: summary.totalLending },
        ].map((s, i) => (
          <Box
            key={i}
            textAlign="center"
            p={0.5}
            px={1}
            minWidth={100}
            sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 1 }}
          >
            <Typography variant="caption">{s.label}</Typography>
            <Typography variant="subtitle2" fontWeight="bold">
              {s.value || 0}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* --- Table --- */}
     <SalesTable 
  rows={salesData} 
  summary={summary} 
  themeColors={tokens(theme.palette.mode)} 
/>

    </Box>
  );
};

export default SalesReport;
